import { ReactNode } from 'react';
import { REGEXES_TO_MATCH, sanitizeLabel } from '../../../../../../../utils/labelSanitization';
import { DescriptorCleanedLabel, ImportTableDescriptorType } from '../../../../types';
import _ from 'lodash';

export function getCleanedLabels(descriptors: ImportTableDescriptorType[]) {
    const result: DescriptorCleanedLabel[] = [];
    // Loop through each descriptor in the map
    descriptors.forEach((d) => {
        // Skip descriptors without a label
        if (!d.label) {
            return;
        }

        const matches: { text: string; index: number }[] = [];
        // Iterate through all predefined regexes and extract matching substrings from the label
        for (const REGEX of REGEXES_TO_MATCH) {
            Array.from(d.label.matchAll(REGEX))
                // Skip empty or space-only matches
                .filter((match) => match[0] && match[0] !== ' ')
                .forEach((match) => {
                    matches.push({ text: match[0], index: match.index! });
                });
        }

        // Skip if no matches were found
        if (!matches.length) {
            return;
        }

        // Sort matches by index (asc) and by length (desc) to ensure correct rendering order
        matches.sort((a, b) => {
            if (a.index === b.index) {
                return b.text.length - a.text.length;
            }

            return a.index - b.index;
        });

        const highlightedLabel: ReactNode[] = [];

        let startIndex = 0;

        // Iterate over each match to break the label into spans
        for (const match of matches) {
            // Skip overlapping matches
            if (match.index < startIndex) {
                continue;
            }

            // Add the non-highlighted portion before the match
            const nonHighlightedPart = d.label.substring(startIndex, match.index);
            highlightedLabel.push(<span key={_.uniqueId()}>{nonHighlightedPart}</span>);

            // Add the matched (highlighted) portion
            const highlightedPart = d.label.substring(match.index, match.index + match.text.length);
            highlightedLabel.push(
                <span key={_.uniqueId()} className="normalized-label_removed-part">
                    {highlightedPart}
                </span>
            );

            // Move the start index forward past the matched portion
            startIndex = match.index + match.text.length;
        }

        // Append the remaining part of the label (if any) after the last match
        if (startIndex < d.label.length) {
            highlightedLabel.push(<span key={_.uniqueId()}>{d.label.substring(startIndex)}</span>);
        }

        // Add the cleaned and annotated label info to the result array
        result.push({
            descriptorId: d.id,
            cleanLabel: sanitizeLabel(d.label),
            include: true,
            highlightedLabel: highlightedLabel,
        });
    });
    return result;
}
